import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  LONGUEUR_CODE,
  demanderCodeWhatsapp,
  libelleRenvoi,
  lirePaysDefaut,
  nettoyerCode,
  secondesRestantes,
  traduireErreurWhatsapp,
  verifierCodeWhatsapp,
} from './api'
import { ChampTelephone, lireTelephone, type ValeurTelephone } from './ChampTelephone'
import { injecterStyles } from './styles'

type Etape = 'numero' | 'code'

/**
 * Connexion par numéro : « Recevoir le code sur WhatsApp » → code à 6 chiffres → session.
 *
 * Deux étapes dans la même carte. La console fournit son client Supabase et reçoit l'identifiant de l'utilisateur
 * connecté dans `surConnecte` (c'est elle qui charge ensuite profil, rôles et organisation, comme après un mot de passe).
 * `creerSiInconnu` : false pour les consoles réservées à des comptes créés par l'équipe ; true pour une inscription libre.
 */
export function ConnexionWhatsapp({
  client,
  surConnecte,
  creerSiInconnu = false,
  libelleBouton = 'Recevoir le code sur WhatsApp',
}: {
  client: SupabaseClient
  surConnecte: (userId: string) => void | Promise<void>
  creerSiInconnu?: boolean
  libelleBouton?: string
}) {
  injecterStyles()
  const [etape, setEtape] = useState<Etape>('numero')
  const [valeur, setValeur] = useState<ValeurTelephone>({ texte: '', iso: 'ML' })
  const [code, setCode] = useState('')
  const [e164, setE164] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [verifie, setVerifie] = useState(false) // « montrer l'erreur du numéro » après un premier essai
  const [envoyeLe, setEnvoyeLe] = useState(0)
  const [maintenant, setMaintenant] = useState(() => Date.now())
  const champCode = useRef<HTMLInputElement | null>(null)

  /* Pays de départ : configuration (jamais écrit en dur), puis dernier choix de l'appareil. */
  useEffect(() => {
    let vivant = true
    void lirePaysDefaut(client).then((p) => vivant && setValeur((v) => (v.texte ? v : { ...v, iso: p.iso })))
    return () => {
      vivant = false
    }
  }, [client])

  /* Compte à rebours du renvoi. */
  useEffect(() => {
    if (etape !== 'code') return
    const t = window.setInterval(() => setMaintenant(Date.now()), 500)
    return () => window.clearInterval(t)
  }, [etape])

  useEffect(() => {
    if (etape === 'code') champCode.current?.focus()
  }, [etape])

  async function envoyer(e?: FormEvent) {
    e?.preventDefault()
    if (occupe) return
    const { e164: numero, resultat } = lireTelephone(valeur)
    if (!numero) {
      setVerifie(true)
      setErreur(resultat && !resultat.ok ? resultat.message : 'Saisissez votre numéro de téléphone.')
      return
    }
    setOccupe(true)
    setErreur(null)
    try {
      await demanderCodeWhatsapp(client, numero, { creerSiInconnu })
      setE164(numero)
      setCode('')
      const t = Date.now()
      setEnvoyeLe(t)
      setMaintenant(t)
      setEtape('code')
    } catch (err) {
      setErreur(traduireErreurWhatsapp(err).message)
    } finally {
      setOccupe(false)
    }
  }

  async function valider(e?: FormEvent) {
    e?.preventDefault()
    if (occupe || !e164) return
    if (code.length !== LONGUEUR_CODE) {
      setErreur(`Saisissez les ${LONGUEUR_CODE} chiffres du code.`)
      return
    }
    setOccupe(true)
    setErreur(null)
    try {
      const uid = await verifierCodeWhatsapp(client, e164, code)
      await surConnecte(uid)
    } catch (err) {
      setErreur(traduireErreurWhatsapp(err, (Date.now() - envoyeLe) / 1000).message)
      setCode('')
      champCode.current?.focus()
    } finally {
      setOccupe(false)
    }
  }

  function surCode(saisie: string) {
    const propre = nettoyerCode(saisie)
    setCode(propre)
    setErreur(null)
    // Le code complet (collage, remplissage automatique) part tout seul : un appui de moins.
    if (propre.length === LONGUEUR_CODE && !occupe && e164) {
      setOccupe(true)
      void verifierCodeWhatsapp(client, e164, propre)
        .then((uid) => surConnecte(uid))
        .catch((err) => {
          setErreur(traduireErreurWhatsapp(err, (Date.now() - envoyeLe) / 1000).message)
          setCode('')
          champCode.current?.focus()
        })
        .finally(() => setOccupe(false))
    }
  }

  const restant = secondesRestantes(envoyeLe, maintenant)

  if (etape === 'numero') {
    return (
      <form className="cw-bloc" onSubmit={envoyer} noValidate>
        <ChampTelephone valeur={valeur} surChangement={(v) => { setValeur(v); setErreur(null) }} montrerErreur={verifie} desactive={occupe} autoFocus
          aide="Vous recevrez un code à 6 chiffres sur WhatsApp." />
        {erreur && <p className="cw-erreur" role="alert">{erreur}</p>}
        <button type="submit" className="cw-bouton" disabled={occupe}>{occupe ? 'Envoi…' : libelleBouton}</button>
      </form>
    )
  }

  return (
    <form className="cw-bloc" onSubmit={valider} noValidate>
      <p className="cw-aide" aria-live="polite">
        Code envoyé sur WhatsApp au <strong>{e164}</strong>.{' '}
        <button type="button" className="cw-lien" onClick={() => { setEtape('numero'); setErreur(null); setCode('') }}>Changer de numéro</button>
      </p>
      <div className="cw-champ">
        <label className="cw-etiquette" htmlFor="cw-code">Code à 6 chiffres</label>
        <input
          id="cw-code"
          ref={champCode}
          className="cw-entree cw-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={LONGUEUR_CODE + 4}
          placeholder="••••••"
          disabled={occupe}
          value={code}
          aria-invalid={erreur ? true : undefined}
          onChange={(e) => surCode(e.target.value)}
          onPaste={(e) => {
            e.preventDefault()
            surCode(e.clipboardData.getData('text'))
          }}
        />
      </div>
      {erreur && <p className="cw-erreur" role="alert">{erreur}</p>}
      <button type="submit" className="cw-bouton" disabled={occupe || code.length !== LONGUEUR_CODE}>{occupe ? 'Vérification…' : 'Se connecter'}</button>
      <div className="cw-rangee">
        <button type="button" className="cw-lien" disabled={occupe || restant > 0} onClick={() => void envoyer()}>{libelleRenvoi(restant)}</button>
      </div>
    </form>
  )
}
